import {
  EndOrderDetails,
  isMarketplaceSource,
  MarketplaceSource,
  OrderPayment,
  Payment,
  PaymentMethodKey,
  PaymentMethodType,
  PaymentType,
} from '@pedix-workspace/utils';
import {
  getCashSessionTotalSold,
  getPaymentMethodKey,
  isSalePayment,
  roundCurrency,
} from './cash-session-amounts';

export { getCashSessionTotalSold as getOrderPaidAmount } from './cash-session-amounts';

export const coversOrderTotal = (paidAmount: number, finalAmount: number): boolean => {
  return roundCurrency(paidAmount) >= roundCurrency(finalAmount);
};

export const getOrderRemainingAmount = (payments: Payment[], finalAmount: number): number => {
  return Math.max(roundCurrency(finalAmount - getCashSessionTotalSold(payments)), 0);
};

export const getOrderPlatformDiscount = (
  order: Partial<Pick<EndOrderDetails, 'marketplaceOrder'>>,
): number => {
  return order.marketplaceOrder?.platformDiscount ?? 0;
};

export const isMarketplaceSettlementPayment = (payment: Pick<Payment, 'methodType'>): boolean =>
  payment.methodType === PaymentMethodType.PEDIDOSYA;

export const getPaymentMarketplaceSource = (
  payment: Pick<Payment, 'methodType' | 'details'>,
): MarketplaceSource | null => {
  const { source } = payment.details;

  if (!source || !isMarketplaceSource(source) || isMarketplaceSettlementPayment(payment)) {
    return null;
  }

  return source;
};

export const buildMarketplaceSettlementPaymentId = (orderId: string): string =>
  `pedidosya_${orderId}`;

export const getCustomerPayments = <T extends Pick<Payment, 'methodType'>>(payments: T[]): T[] =>
  payments.filter(payment => !isMarketplaceSettlementPayment(payment));

export const getPendingMarketplaceSettlement = (
  order: Partial<Pick<EndOrderDetails, 'marketplaceOrder'>>,
  payments: Payment[],
): number => {
  const isAlreadySettled = payments.some(
    payment => isSalePayment(payment) && isMarketplaceSettlementPayment(payment),
  );

  return order.marketplaceOrder?.collectedBy === 'establishment' && !isAlreadySettled
    ? getOrderPlatformDiscount(order)
    : 0;
};

export const getOrderAmountDue = (
  order: Pick<EndOrderDetails, 'finalAmount'> & Partial<Pick<EndOrderDetails, 'marketplaceOrder'>>,
): number => {
  return Math.max(roundCurrency((order.finalAmount ?? 0) - getOrderPlatformDiscount(order)), 0);
};

export const getOrderOutstandingAmount = (
  order: Pick<EndOrderDetails, 'finalAmount' | 'paidAmount'> &
    Partial<Pick<EndOrderDetails, 'marketplaceOrder'>>,
): number => {
  return Math.max(roundCurrency(getOrderAmountDue(order) - (order.paidAmount ?? 0)), 0);
};

export const getOrderCollectedAmount = (
  order: Pick<EndOrderDetails, 'paidAmount' | 'digitalPayment'>,
): number => {
  return order.paidAmount ?? order.digitalPayment?.paidAmount ?? 0;
};

export const getOrderPaidBalance = (paidAmount: number, finalAmount: number): number => {
  return roundCurrency(paidAmount - finalAmount);
};

export const getOrderSurplusAmount = (
  paidAmount: number,
  finalAmount: number | undefined,
): number => {
  if (finalAmount === undefined) {
    return 0;
  }
  return Math.max(getOrderPaidBalance(paidAmount, finalAmount), 0);
};

export const getOrderRefundableAmount = (
  paidAmount: number,
  finalAmount: number | undefined,
): number => {
  return Math.max(roundCurrency(paidAmount - getOrderSurplusAmount(paidAmount, finalAmount)), 0);
};

export const getSuggestedRefundAmount = (
  paidAmount: number,
  finalAmount: number | undefined,
): number => {
  const surplus = getOrderSurplusAmount(paidAmount, finalAmount);
  const refundable = getOrderRefundableAmount(paidAmount, finalAmount);

  return surplus > 0 ? Math.min(surplus, refundable) : refundable;
};

export type OrderRefundByMethod = {
  methodType: PaymentMethodType;
  methodName?: string;
  amount: number;
};

export const buildOrderRefundsByMethod = (
  payments: OrderPayment[],
  maxRefundTotal?: number,
): OrderRefundByMethod[] => {
  const salesByMethodKey = new Map<
    string,
    { methodType: PaymentMethodType; methodName?: string; netAmount: number }
  >();

  for (const payment of payments) {
    if (!isSalePayment(payment)) {
      continue;
    }
    const methodKey = getPaymentMethodKey(payment);
    const group = salesByMethodKey.get(methodKey) ?? {
      methodType: payment.methodType,
      methodName: payment.details?.methodName,
      netAmount: 0,
    };
    group.netAmount = roundCurrency(group.netAmount + payment.amount);
    salesByMethodKey.set(methodKey, group);
  }

  const refunds: OrderRefundByMethod[] = [];
  let remainingBudget = maxRefundTotal;

  for (const group of salesByMethodKey.values()) {
    const amount =
      remainingBudget === undefined
        ? roundCurrency(group.netAmount)
        : roundCurrency(Math.min(group.netAmount, remainingBudget));

    if (amount <= 0) {
      continue;
    }

    refunds.push({
      methodType: group.methodType,
      ...(group.methodName ? { methodName: group.methodName } : {}),
      amount,
    });

    if (remainingBudget !== undefined) {
      remainingBudget = roundCurrency(remainingBudget - amount);
    }
  }

  return refunds;
};

const ORDER_PAYMENT_TYPE_RANK: Partial<Record<PaymentType, number>> = {
  sale: 0,
  tip: 1,
  refund: 2,
};

export const compareOrderPayments = (
  a: Pick<Payment, 'at' | 'type'>,
  b: Pick<Payment, 'at' | 'type'>,
): number => {
  return (
    a.at.getTime() - b.at.getTime() ||
    (ORDER_PAYMENT_TYPE_RANK[a.type] ?? 0) - (ORDER_PAYMENT_TYPE_RANK[b.type] ?? 0)
  );
};

export const buildOrderPayments = (payments: Payment[]): OrderPayment[] => {
  return [...payments].sort(compareOrderPayments).map(payment => ({
    id: payment.id,
    type: payment.type,
    methodType: payment.methodType,
    amount: payment.amount,
    at: payment.at,
    details: payment.details?.methodName ? { methodName: payment.details.methodName } : {},
  }));
};

export const isSameOrderPayments = (
  current: OrderPayment[] | undefined,
  next: OrderPayment[],
): boolean => {
  const toSignature = (payments: OrderPayment[]) =>
    payments
      .map(payment =>
        [
          payment.id,
          payment.type,
          payment.methodType,
          payment.details?.methodName,
          payment.amount,
        ].join('|'),
      )
      .join(',');

  return toSignature(current ?? []) === toSignature(next);
};

export type OrderCollectedAmountByMethod = {
  methodKey: PaymentMethodKey;
  amount: number;
};

const toPaymentMethodKey = ({ methodType, methodName }: OrderRefundByMethod): PaymentMethodKey => {
  return getPaymentMethodKey({ methodType, details: { methodName } });
};

export const hasOrderSalePayments = (payments: OrderPayment[] | undefined): boolean => {
  return (payments ?? []).some(isSalePayment);
};

export const getOrderCollectedAmountsByMethod = (
  payments: OrderPayment[] | undefined,
  finalAmount: number,
): OrderCollectedAmountByMethod[] => {
  return buildOrderRefundsByMethod(payments ?? [], finalAmount).map(netByMethod => ({
    methodKey: toPaymentMethodKey(netByMethod),
    amount: netByMethod.amount,
  }));
};

export const getOrderCollectedMethodKeys = (
  payments: OrderPayment[] | undefined,
): PaymentMethodKey[] => {
  const orderPayments = payments ?? [];
  const netMethodKeys = buildOrderRefundsByMethod(orderPayments).map(toPaymentMethodKey);

  if (netMethodKeys.length > 0) {
    return netMethodKeys;
  }
  return [
    ...new Set(orderPayments.filter(payment => payment.type === 'sale').map(getPaymentMethodKey)),
  ];
};
